import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { XtreedLogo } from '@/components/ui/XtreedLogo';
import { NotificationDropdown } from '@/components/ui/NotificationDropdown';
import { LanguageSelector } from '@/components/LanguageSelector';
import {
  LayoutDashboard,
  BookOpen,
  User,
  LogOut,
  Menu,
  X,
  Award,
  Trophy,
  ChevronRight,
  Moon,
  Sun,
  ExternalLink,
  Globe,
  MessageSquare,
  HelpCircle,
  FileText,
  Video,
  Music,
  Image as ImageIcon,
  Calendar,
  Mail,
  Phone,
  MapPin,
  ShoppingCart,
  Heart,
  Star,
  Zap,
  Users,
  Gift,
  Megaphone,
  Shield,
  ChevronDown,
  PanelLeftClose,
  PanelLeftOpen,
  type LucideIcon,
} from 'lucide-react';
import {
  usePublicMenuItems,
  usePublicGamificationSettings,
  useCourseBySlug,
  useMyPoints,
  useLogout,
} from '@/hooks';
import { cn } from '@/lib/utils';
import { WhatsAppBubble } from '@/components/ui/WhatsAppBubble';
import { GamificationWidget } from '@/components/student/GamificationWidget';
import { SiteBannerCarousel } from '@/components/student/SiteBannerCarousel';
import { GamifiedAvatar } from '@/components/ui/GamifiedAvatar';
import { useRecursoAtivo } from '@/hooks/useFeatureFlag';
import { useLocaleFormat } from '@/i18n/locale';
import { useStudentTheme } from '@/hooks/useStudentTheme';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@xtreed/academy-shared/ui';

const sidebarIconMap: Record<string, LucideIcon> = {
  LayoutDashboard,
  BookOpen,
  Award,
  User,
  Globe,
  ExternalLink,
  MessageSquare,
  HelpCircle,
  FileText,
  Video,
  Music,
  Image: ImageIcon,
  Calendar,
  Mail,
  Phone,
  MapPin,
  ShoppingCart,
  Heart,
  Star,
  Zap,
  Users,
  Gift,
  Megaphone,
};

interface StudentLayoutProps {
  /** Renders the layout as an in-place appearance editor. */
  editMode?: boolean;
  /** Editable sidebar nav, rendered in place of the menu when in edit mode. */
  sidebarSlot?: ReactNode;
  /** Main content; falls back to the routed <Outlet/> when omitted. */
  children?: ReactNode;
}

export function StudentLayout({
  editMode = false,
  sidebarSlot,
  children,
}: StudentLayoutProps = {}) {
  const { t } = useTranslation('layout');
  const { intlLocale } = useLocaleFormat();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [desktopSidebarOpen, setDesktopSidebarOpen] = useState(() => {
    const stored = localStorage.getItem('desktop-sidebar-open');
    return stored === null ? true : stored === 'true';
  });
  /**
   * Modo recolhido: a barra encolhe para uma faixa de ícones. É estado separado
   * de `desktopSidebarOpen` (que esconde a barra por completo) porque são coisas
   * diferentes — recolhido continua navegável, oculto libera a largura toda.
   */
  const [sidebarRecolhida, setSidebarRecolhida] = useState(() => {
    return localStorage.getItem('sidebar-recolhida') === 'true';
  });
  /** Bloco de progresso: fechado por padrão, para o menu começar curto. */
  const [progressoAberto, setProgressoAberto] = useState(() => {
    return localStorage.getItem('sidebar-progresso-aberto') === 'true';
  });
  const {
    isDark,
    canToggle: canToggleTheme,
    toggle: toggleTheme,
  } = useStudentTheme();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024);
  const location = useLocation();
  const { user } = useAuthStore();
  const { mutate: logout } = useLogout();
  const { data: customMenuItems, isLoading: menuLoading } =
    usePublicMenuItems();
  const { data: publicGamificationSettings } = usePublicGamificationSettings();

  // When viewing a specific course (/courses/:slug[/...]), a per-course sidebar
  // overrides the global menu. The query key matches CoursePage, so it's shared.
  const courseSlugMatch = location.pathname.match(/^\/courses\/([^/]+)/);
  const activeCourseSlug = courseSlugMatch?.[1];
  const { data: activeCourse } = useCourseBySlug(
    activeCourseSlug || '',
    !!activeCourseSlug,
  );
  const courseMenuItems = activeCourse?.menuItems;
  const hasCourseMenu = !!(courseMenuItems && courseMenuItems.length > 0);
  // Unified list the nav renders: per-course menu wins when present.
  const effectiveMenuItems = hasCourseMenu ? courseMenuItems : customMenuItems;

  const gamificationSettingsData =
    (publicGamificationSettings as any)?.data || publicGamificationSettings;
  const gamificationEnabled = gamificationSettingsData?.enabled;
  // A Xtreed liga a comunidade por cliente; sem ela o item nem aparece.
  const communityActive = useRecursoAtivo('community');
  const certificatesActive = useRecursoAtivo('certificates');

  /**
   * Preferência do balão de WhatsApp, por usuário.
   *
   * O interruptor saiu da barra lateral (ocupava três linhas para algo que se
   * ajusta uma vez), mas a preferência continua valendo — quem já desligou
   * antes segue com ele desligado. Quem alterna hoje é a tela de Configurações.
   */
  const bubbleKey = user?.id ? `whatsapp-bubble-${user.id}` : null;
  const [showBubble] = useState(() => {
    if (!bubbleKey) return true;
    const stored = localStorage.getItem(bubbleKey);
    return stored === null ? true : stored === 'true';
  });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleDesktopSidebar = useCallback(() => {
    setDesktopSidebarOpen((prev) => {
      const next = !prev;
      localStorage.setItem('desktop-sidebar-open', String(next));
      return next;
    });
  }, []);

  const alternarRecolhida = useCallback(() => {
    setSidebarRecolhida((prev) => {
      const next = !prev;
      localStorage.setItem('sidebar-recolhida', String(next));
      return next;
    });
  }, []);

  const alternarProgresso = useCallback(() => {
    setProgressoAberto((prev) => {
      const next = !prev;
      localStorage.setItem('sidebar-progresso-aberto', String(next));
      return next;
    });
  }, []);

  /** No mobile a barra é uma gaveta: sempre completa quando aberta. */
  const recolhida = sidebarRecolhida && !isMobile;

  // Nível e XP no cabeçalho do bloco: é a informação que a pessoa quer de
  // relance, sem precisar abrir o painel inteiro.
  const { data: meusPontos } = useMyPoints();
  const pontos = (meusPontos as any)?.data || meusPontos;
  const xpTotal: number = pontos?.totalPoints ?? 0;
  const nivelAtual = pontos?.level as { name?: string } | undefined;

  return (
    <div className="min-h-screen overflow-x-hidden bg-background">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 animate-fade-in bg-black/60 backdrop-blur-xs lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 transform transition-all duration-300 ease-smooth',
          'flex flex-col border-r border-border/50 bg-card/95 backdrop-blur-xl',
          // Gaveta no mobile mantém a largura cheia; no desktop, 240px expandida
          // (era 288) ou 72px recolhida, só com os ícones.
          'w-72',
          recolhida ? 'sidebar-recolhida lg:w-[72px]' : 'lg:w-60',
          // Mobile: controlled by sidebarOpen
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
          // Desktop: controlled by desktopSidebarOpen
          desktopSidebarOpen ? 'lg:translate-x-0' : 'lg:-translate-x-full',
        )}
      >
        {/* Gradient line at top */}
        <div className="absolute top-0 right-0 left-0 h-[2px] bg-linear-to-r from-transparent via-primary to-transparent" />

        {/* Sidebar Header */}
        <div
          className={cn(
            'flex h-16 shrink-0 items-center border-b border-border/50',
            recolhida ? 'justify-center px-2' : 'justify-between px-4',
          )}
        >
          <Link to="/home" className="group flex items-center gap-3">
            <XtreedLogo size="sm" showText={!recolhida} />
          </Link>
          {/* Mobile close */}
          <button
            className="rounded-lg p-2 transition-colors hover:bg-secondary/80 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-5 w-5" />
          </button>
          {/* Desktop: alterna entre recolhida e expandida. */}
          {!recolhida && (
            <button
              className="hidden rounded-lg p-2 transition-colors hover:bg-secondary/80 lg:flex"
              onClick={alternarRecolhida}
              title={t('student.collapseMenu')}
              aria-label={t('student.collapseMenu')}
            >
              <PanelLeftClose className="h-4 w-4 text-muted-foreground transition-colors hover:text-foreground" />
            </button>
          )}
        </div>

        {/* Recolhida: o botão de expandir fica sozinho, abaixo do logo. */}
        {recolhida && (
          <div className="hidden shrink-0 justify-center border-b border-border/50 py-2 lg:flex">
            <button
              className="rounded-lg p-2 transition-colors hover:bg-secondary/80"
              onClick={alternarRecolhida}
              title={t('student.expandMenu')}
              aria-label={t('student.expandMenu')}
            >
              <PanelLeftOpen className="h-4 w-4 text-muted-foreground transition-colors hover:text-foreground" />
            </button>
          </div>
        )}

        {/*
          Progresso do aluno, recolhido por padrão.
          O nome e o e-mail saíram: são a informação que a pessoa menos precisa
          reler (ela já sabe quem é), e ocupavam a faixa mais nobre da barra. A
          identificação continua no avatar e no menu da conta, no topo.
        */}
        {gamificationEnabled && !recolhida && (
          <div className="shrink-0 border-b border-border/50">
            <button
              onClick={alternarProgresso}
              aria-expanded={progressoAberto}
              className="flex w-full items-center gap-2.5 px-3 py-2.5 transition-colors hover:bg-muted/40"
            >
              <GamifiedAvatar
                src={user?.avatar}
                name={user?.name}
                size="sm"
                showLevelBadge
              />
              <span className="min-w-0 flex-1 text-left">
                <span className="block truncate text-sm font-medium text-foreground">
                  {nivelAtual?.name ?? t('student.defaultLevel')}
                </span>
                <span className="block font-mono text-[11px] text-muted-foreground tabular-nums">
                  {t('student.xp', {
                    value: xpTotal.toLocaleString(intlLocale),
                  })}
                </span>
              </span>
              <ChevronDown
                className={cn(
                  'h-4 w-4 text-muted-foreground transition-transform duration-200',
                  progressoAberto && 'rotate-180',
                )}
              />
            </button>
            {progressoAberto && <GamificationWidget />}
          </div>
        )}

        {/* Recolhida: só o avatar, que já carrega o nível. */}
        {recolhida && (
          <div
            className="flex shrink-0 justify-center border-b border-border/50 p-2"
            title={user?.name}
          >
            <GamifiedAvatar
              src={user?.avatar}
              name={user?.name}
              size="sm"
              showLevelBadge
            />
          </div>
        )}

        {/* Navigation - scrollable area */}
        <nav
          className={cn(
            'flex-1 space-y-0.5 overflow-y-auto',
            recolhida ? 'px-2 py-3' : 'p-3',
          )}
        >
          {!recolhida && (
            <p className="mb-2 px-3 text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
              {t('student.menu')}
            </p>
          )}

          {/* Dynamic Menu Items from Database */}
          {editMode && sidebarSlot ? (
            sidebarSlot
          ) : menuLoading ? (
            /* Skeleton while loading — prevents flash of fallback menu */
            Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="flex items-center gap-3 rounded-lg px-3 py-2.5"
              >
                <div className="h-8 w-8 animate-pulse rounded-lg bg-secondary/50" />
                <div className="h-4 w-24 animate-pulse rounded bg-secondary/50" />
              </div>
            ))
          ) : effectiveMenuItems &&
            Array.isArray(effectiveMenuItems) &&
            effectiveMenuItems.length > 0 ? (
            [...effectiveMenuItems]
              .sort((a, b) => a.order - b.order)
              .map((item, index) => {
                const IconComponent = sidebarIconMap[item.icon || ''] || Globe;
                const isExt = item.isExternal || item.openNewTab;

                if (isExt) {
                  return (
                    <a
                      key={item.id}
                      href={item.url || '#'}
                      target={item.openNewTab ? '_blank' : undefined}
                      rel={item.openNewTab ? 'noopener noreferrer' : undefined}
                      style={{ animationDelay: `${index * 50}ms` }}
                      className="nav-item group animate-fade-in text-muted-foreground hover:text-foreground"
                    >
                      <div className="rounded-lg bg-secondary/50 p-2 transition-all duration-200 group-hover:bg-primary/10">
                        <IconComponent className="h-4 w-4 transition-colors group-hover:text-primary-ink" />
                      </div>
                      <span className="flex-1 font-medium">{item.title}</span>
                      <ExternalLink className="h-3 w-3 text-muted-foreground opacity-50" />
                    </a>
                  );
                }

                return (
                  <Link
                    key={item.id}
                    to={item.url || '#'}
                    style={{ animationDelay: `${index * 50}ms` }}
                    className={cn(
                      'nav-item group animate-fade-in',
                      location.pathname === item.url
                        ? 'active'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    <div
                      className={cn(
                        'rounded-lg p-2 transition-all duration-200',
                        location.pathname === item.url
                          ? 'bg-primary/20'
                          : 'bg-secondary/50 group-hover:bg-primary/10',
                      )}
                    >
                      <IconComponent
                        className={cn(
                          'h-4 w-4 transition-colors',
                          location.pathname === item.url
                            ? 'text-primary-ink'
                            : 'group-hover:text-primary-ink',
                        )}
                      />
                    </div>
                    <span className="flex-1 font-medium">{item.title}</span>
                    {location.pathname === item.url && (
                      <ChevronRight className="h-4 w-4 text-primary-ink" />
                    )}
                  </Link>
                );
              })
          ) : (
            <>
              {[
                {
                  title: t('student.nav.home'),
                  url: '/home',
                  Icon: LayoutDashboard,
                },
                {
                  title: t('student.nav.courses'),
                  url: '/courses',
                  Icon: BookOpen,
                },
                ...(gamificationEnabled
                  ? [
                      {
                        title: t('student.nav.ranking'),
                        url: '/ranking',
                        Icon: Trophy,
                      },
                    ]
                  : []),
                ...(communityActive
                  ? [
                      {
                        title: t('student.nav.community'),
                        url: '/community',
                        Icon: Users,
                      },
                    ]
                  : []),
                {
                  title: t('student.nav.certificates'),
                  url: '/certificates',
                  Icon: Award,
                  comingSoon: !certificatesActive,
                },
                {
                  title: t('student.nav.profile'),
                  url: '/profile',
                  Icon: User,
                },
              ].map((item, index) =>
                'comingSoon' in item && item.comingSoon ? (
                  <div
                    key={item.url}
                    aria-disabled="true"
                    title={`${item.title} · ${t('student.comingSoon')}`}
                    style={{ animationDelay: `${index * 50}ms` }}
                    className="nav-item animate-fade-in cursor-default text-muted-foreground/60"
                  >
                    <div className="rounded-lg bg-secondary/30 p-2">
                      <item.Icon className="h-4 w-4" />
                    </div>
                    <span className="flex min-w-0 flex-1 flex-col leading-tight">
                      <span className="truncate font-medium">{item.title}</span>
                      <span className="truncate text-[10px] font-semibold tracking-wide text-muted-foreground/70 uppercase">
                        {t('student.comingSoon')}
                      </span>
                    </span>
                  </div>
                ) : (
                  <Link
                    key={item.url}
                    to={item.url}
                    style={{ animationDelay: `${index * 50}ms` }}
                    className={cn(
                      'nav-item group animate-fade-in',
                      location.pathname === item.url
                        ? 'active'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    <div
                      className={cn(
                        'rounded-lg p-2 transition-all duration-200',
                        location.pathname === item.url
                          ? 'bg-primary/20'
                          : 'bg-secondary/50 group-hover:bg-primary/10',
                      )}
                    >
                      <item.Icon
                        className={cn(
                          'h-4 w-4 transition-colors',
                          location.pathname === item.url
                            ? 'text-primary-ink'
                            : 'group-hover:text-primary-ink',
                        )}
                      />
                    </div>
                    <span className="flex-1 font-medium">{item.title}</span>
                    {location.pathname === item.url && (
                      <ChevronRight className="h-4 w-4 text-primary-ink" />
                    )}
                  </Link>
                ),
              )}
            </>
          )}
        </nav>

        {/* Banners: não cabem na faixa de 72px do modo recolhido. */}
        {!recolhida && (
          <div className="px-3 pb-2">
            <SiteBannerCarousel
              position="sidebar"
              compact
              autoPlayInterval={7000}
            />
          </div>
        )}

        {/*
          Rodapé enxuto: só o sair.
          O interruptor do WhatsApp saiu daqui — ocupava três linhas na barra
          para uma preferência que se mexe uma vez. Ele continua disponível em
          Configurações; o que se controlava aqui é o mesmo estado.
        */}
        <div className="shrink-0 border-t border-border/50 bg-card/80 p-2 backdrop-blur-xs">
          <button
            onClick={() => logout()}
            title={recolhida ? t('student.logout') : undefined}
            className={cn(
              'text-muted-foreground hover:bg-destructive/10 hover:text-destructive',
              'flex w-full items-center rounded-lg py-2 text-sm font-medium transition-colors',
              recolhida ? 'justify-center px-0' : 'gap-2.5 px-3',
            )}
          >
            <LogOut className="h-4 w-4 shrink-0" />
            {!recolhida && <span>{t('student.logout')}</span>}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div
        className={cn(
          'flex min-h-screen flex-col overflow-x-hidden transition-all duration-300',
          // Acompanha a largura da barra, senão o conteúdo fica por baixo dela.
          desktopSidebarOpen
            ? recolhida
              ? 'lg:pl-[72px]'
              : 'lg:pl-60'
            : 'lg:pl-0',
        )}
      >
        {/* Header */}
        <header className="sticky top-0 z-30 h-16 border-b border-border/50 bg-background/80 backdrop-blur-xl">
          <div className="flex h-full items-center justify-between px-4 lg:px-6">
            <div className="flex items-center gap-4">
              {/* Mobile menu button */}
              <button
                className="rounded-lg p-2 transition-colors hover:bg-secondary/80 lg:hidden"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="h-5 w-5" />
              </button>
              {/* Desktop sidebar toggle (visible when sidebar is closed) */}
              {!desktopSidebarOpen && (
                <button
                  className="hidden rounded-lg p-2 transition-colors hover:bg-secondary/80 lg:flex"
                  onClick={toggleDesktopSidebar}
                  title={t('student.openMenu')}
                >
                  <PanelLeftOpen className="h-5 w-5 text-muted-foreground transition-colors hover:text-foreground" />
                </button>
              )}

              {/* Welcome message */}
              <div className="hidden items-center gap-2 sm:flex">
                <Trans
                  t={t}
                  i18nKey="student.welcome"
                  values={{ name: user?.name?.split(' ')[0] }}
                  components={{
                    muted: <span className="text-muted-foreground" />,
                    name: <span className="font-semibold text-foreground" />,
                  }}
                />
                <span className="text-2xl">👋</span>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              {/* Admin Panel Button - only for admins */}
              {(user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN') && (
                <Link
                  to="/admin"
                  className="group flex items-center gap-2 rounded-xl border border-primary/30 bg-linear-to-r from-primary/20 to-primary/5 px-3 py-1.5 transition-all duration-200 hover:border-primary/50 hover:from-primary/30 hover:to-primary/10"
                >
                  <Shield className="h-4 w-4 text-primary-ink transition-transform group-hover:scale-110" />
                  <span className="hidden text-sm font-medium text-primary-ink sm:inline">
                    {t('student.admin')}
                  </span>
                </Link>
              )}

              {canToggleTheme && (
                <button
                  onClick={toggleTheme}
                  className="rounded-lg p-2 transition-colors hover:bg-secondary/80"
                  title={
                    isDark ? t('student.lightMode') : t('student.darkMode')
                  }
                  aria-label={
                    isDark ? t('student.lightMode') : t('student.darkMode')
                  }
                >
                  {isDark ? (
                    <Sun className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Moon className="h-4 w-4 text-muted-foreground" />
                  )}
                </button>
              )}

              {/* Notifications */}
              <NotificationDropdown />

              <LanguageSelector />

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    aria-label={t('student.myProfile')}
                    className="rounded-full ring-2 ring-transparent transition-all duration-200 hover:ring-primary/30 hover:ring-offset-1 hover:ring-offset-transparent focus:outline-hidden"
                  >
                    <GamifiedAvatar
                      src={user?.avatar}
                      name={user?.name}
                      size="sm"
                    />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-60 rounded-xl border border-border/50 bg-card/95 p-1 shadow-xl backdrop-blur-xl"
                >
                  <div className="mb-1 rounded-lg bg-linear-to-r from-primary/10 to-primary/5 px-3 py-3">
                    <p className="truncate font-semibold">{user?.name}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {user?.email}
                    </p>
                  </div>
                  <DropdownMenuItem asChild>
                    <Link
                      to="/profile"
                      className="cursor-pointer rounded-lg px-3 py-2.5 text-foreground hover:bg-muted focus:bg-muted focus:text-foreground"
                    >
                      <User className="mr-3 h-4 w-4 text-primary-ink" />
                      {t('student.myProfile')}
                    </Link>
                  </DropdownMenuItem>
                  {certificatesActive && (
                    <DropdownMenuItem asChild>
                      <Link
                        to="/certificates"
                        className="cursor-pointer rounded-lg px-3 py-2.5 text-foreground hover:bg-muted focus:bg-muted focus:text-foreground"
                      >
                        <Award className="mr-3 h-4 w-4 text-primary-ink" />
                        {t('student.nav.certificates')}
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="my-1 bg-border/50" />
                  <DropdownMenuItem
                    onClick={() => logout()}
                    className="cursor-pointer rounded-lg px-3 py-2.5 text-destructive hover:bg-destructive/10 focus:bg-destructive/10 focus:text-destructive"
                  >
                    <LogOut className="mr-3 h-4 w-4" />
                    {t('student.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Header Banners */}
        <SiteBannerCarousel
          position="header"
          className="mx-4 mt-4 w-auto md:mx-5 md:mt-5 lg:mx-6 lg:mt-6"
        />

        {/* Page content */}
        <main className="flex-1 p-4 md:p-5 lg:p-6">
          <div className="animate-fade-in">{children ?? <Outlet />}</div>
        </main>
      </div>

      {/* WhatsApp Bubble */}
      {showBubble && !editMode && <WhatsAppBubble />}
    </div>
  );
}
